import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpRoutingService } from './http-routing.service';
import { environment } from '../../../environments/environment';
import { ApiResponse, Countries, Country, IdModel, Document, ProfileImage, CheckEmailRes, checkEmailReq } from '../models/common.model';
import { EnableTFAReq } from '../../auth/models/auth.model';
import { AuthService } from '../../auth/services/auth.service';
import { ToastService } from '../../shared/services/toast-service';
import { ToastType } from '../../shared/models/toast.model';
@Injectable({
  providedIn: 'root'
})
export class CommonService {
  /**
   * Variable which is used to store and retrive the values.
   */
  countries = new BehaviorSubject<string[]>([]);
  /**
   * Variable which is used to store and retrive the countries data.
   */
  countryAndStates: Countries[] = [];
  /**
   * Variable which is used to check the canDeactivate result.
   */
  navLoader = new BehaviorSubject<boolean>(true);
  /**
   * Constructor used to inject the required services.
   * @param http To access the functions inside HttpRoutingService.
   * @param authService To access the functions inside AuthService.
   * @param toastService To access the functions inside ToastService.
   */
  constructor(
    private http: HttpRoutingService,
    private authService: AuthService,
    private toastService: ToastService) {
  }
  /**
   * Method which is used to encrypt the param value.
   * @param data Parameter which is used to hold the data to encrypt.
   */
  setParam(data: number | string) {
    return CryptoJS.AES.encrypt(data.toString(), environment.secretKey).toString();
  }
  /**
   * Method which is used to decrypt the param value.
   * @param ciphertext Parameter Which is used to hold the data to decrypt.
   */
  getParam(ciphertext: any) {
    const bytes = CryptoJS.AES.decrypt(ciphertext.toString(), environment.secretKey);
    return bytes.toString(CryptoJS.enc.Utf8);
  }
  /**
   * Method which is used to update customer status.
   * @param data The data which holds customer details.
   * @return {Observable<ApiResponse<null>>}
   */
  updateCustomerStatus(data: IdModel): Observable<ApiResponse<null>> {
    return this.http.postMethod('updateCustomerStatus', data) as Observable<ApiResponse<null>>;
  }
  /**
   * Method which is used to change profile picture.
   * @param data The data which holds image name and image url.
   * @return {Observable<ApiResponse<null>>}
   */
  uploadProfilePicture(data: ProfileImage): Observable<ApiResponse<null>> {
    return this.http.postMethod('uploadProfilePicture', data) as Observable<ApiResponse<null>>;
  }
  /**
   * Method which is used to update two factor authentication status.
   * @param data The data which holds TFA detail.
   * @return {Observable<ApiResponse<null>>}
   */
  enableTFA(data: EnableTFAReq): Observable<ApiResponse<null>> {
    return this.http.postMethod('enableTFA', data) as Observable<ApiResponse<null>>;
  }
  /**
   * Method which is used to get all the existing countries details from the database.
   * returns the response from the Json data.
   */
  getCountries() {
    this.http.getJsonData('/countries.json').subscribe((res: Country) => {
      if (res) {
        this.countryAndStates = res.countries;
        const country: string[] = res.countries.map(item => item.country);
        this.countries.next(country);
      }
    });
  }
  /**
   * Method which is used to get all the existing states details from the database.
   * @param name Parameter which is used to Pass the value inside the function.
   */
  getStates(name: string): string[] {
    let state: string[] = [];
    const index: number = this.countryAndStates.findIndex(item => item.country === name);
    if (index !== -1) {
      state = this.countryAndStates[index].states;
    }
    return state;
  }
  /**
   * Method which is used to read the file data as url.
   * @param event To store the file data.
   */
  setDocumentData(event: Event): Promise<Document> {
    const files = event.target as HTMLInputElement;
    const file = files.files?.[0];
    if (!file) {
      return Promise.resolve({} as Document);
    }
    const reader = new FileReader();
    const doc = {} as Document;
    const ext = file.name.substring(file.name.lastIndexOf('.') + 1);
    doc.ext = ext.toLowerCase();
    doc.size = file.size;
    doc.name = file.name;
    return new Promise((resolve) => {
      reader.readAsDataURL(file);
      reader.onload = (data) => {
        const fullBase64 = reader.result as string;
        doc.url = fullBase64;
        doc.base64textString = fullBase64;
        resolve(doc);
      };
    });
  }
  /**
   * Method which is used to read multple file data as url.
   * @param event To store the file data.
   */
  setMultipleDocumentData(event: Event): Promise<Document[]> {
    const document: Document[] = [];
    const fileEvent: HTMLInputElement = event.target as HTMLInputElement;
    const files = fileEvent.files ? Array.from(fileEvent.files) : [];
    if (files.length) {
      const fileReader: FileReader[] = [];
      const reader: FileReader[] = [];
      return new Promise((resolve) => {
        for (let i = 0; i < files.length; i++) {
          const doc = {} as Document;
          reader[i] = new FileReader();
          fileReader[i] = new FileReader();
          const ext = files[i].name.substr(files[i].name.lastIndexOf('.') + 1);
          doc.ext = ext.toLowerCase();
          doc.name = files[i].name;
          doc.size = files[i].size;
          reader[i].readAsDataURL(files[i]);
          reader[i].onload = (data) => {
            doc.url = data.target!.result as string;
          };
          fileReader[i].readAsBinaryString(files[i]);
          fileReader[i].onload = (data) => {
            doc.base64textString = btoa(data.target!.result as string);
          };
          document.push(doc);
        }
        resolve(document);
      });
    } else {
      return Promise.resolve([]);
    }
  }
  /**
   * Method which is used to check whether the email already used or not.
   * @param email {string} which holds the email id to check.
   * @param id which holds the id.
   * @return {Observable<ApiResponse<CheckEmailRes>> }
   */
  checkEmail(email: string, id: number | null): Observable<ApiResponse<CheckEmailRes>> {
    const data: checkEmailReq = { email };
    if (id) { data.id = id; }
    return this.http.postMethod('checkEmail', data) as Observable<ApiResponse<CheckEmailRes>>;
  }
  /**
   * Method which is used to check the file format and size.
   * @param event To store the file data.
   * @param fileFormat To store the file extension config.
   * @param size To store the file's size config.
   */
  checkFileFormat(event: Event, fileFormat: string[], size: number): boolean {
    const messages: { [key: string]: string } = this.authService.errorMessages;
    const input = event.target as HTMLInputElement;
    if (!input?.files?.length) return false;
    const file = input.files[0];
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!fileFormat.includes(ext)) {
      this.toastService.show({ type: ToastType.Error, message: messages.FORMAT_TYPE });
      return false;
    }
    if ((file.size / 1024 / 1024) > size) {
      const toastMessage: string = messages.INVALID_FILE_SIZE.replace('{x}', size.toString());
      this.toastService.show({ type: ToastType.Error, message: toastMessage });
      return false;
    }
    return true;
  }
  /**
   * Method which is used to format the file size in a human-readable format.
   * @param bytes {number} which holds the file size in bytes.
   * @param decimals {number} which holds the number of decimal places to display.
   * @return {string} which holds the formatted file size.
   */
  formatBytes(bytes: number, decimals: number = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  }
}
